import { sectionRenderer, normalizeSectionId } from "./pent-core/section-renderer.js"
import { morph } from "./pent-core/morph.js"
import { RecentlyViewed } from "./pl--recently-viewed.js"

/**
 * PlPredictiveSearch - Custom element for predictive search functionality
 * Listens for search:query events and fetches/displays search results
 */
class PlPredictiveSearch extends HTMLElement {
  #resultsContainer = null
  #activeFetch = null
  #currentIndex = -1
  #controller = new AbortController()

  connectedCallback() {
    this.#resultsContainer = this.querySelector("[data-search-results]")

    if (!this.#resultsContainer) {
      console.error("PlPredictiveSearch: No [data-search-results] container found")
      return
    }

    // Listen for search:query events from pl-search-input
    document.addEventListener("search:query", this.#handleSearchQuery.bind(this), {
      signal: this.#controller.signal,
    })

    // Listen for search:cleared events
    document.addEventListener("search:cleared", this.#handleSearchCleared.bind(this), {
      signal: this.#controller.signal,
    })

    // Listen for drawer close to reset
    const drawer = this.closest("pl-search-drawer")
    if (drawer) {
      drawer.addEventListener("pl-dialog:closed", this.#resetSearch.bind(this), {
        signal: this.#controller.signal,
      })
    }

    // Listen for explicit reset events
    document.addEventListener("search:reset", this.#resetSearch.bind(this), {
      signal: this.#controller.signal,
    })

    // Initialize with empty state
    this.#resetSearch()

    // Keyboard navigation
    this.addEventListener("keydown", this.#handleKeyboardNavigation.bind(this), {
      signal: this.#controller.signal,
    })
  }

  disconnectedCallback() {
    this.#controller.abort()
    if (this.#activeFetch) {
      this.#activeFetch.abort()
    }
  }

  /**
   * Handles search:query events
   * @param {CustomEvent} event - The search:query event
   */
  #handleSearchQuery(event) {
    const { query } = event.detail

    if (!query || !query.trim()) {
      this.#resetSearch()
      return
    }

    this.#getSearchResults(query.trim())
  }

  /**
   * Handles search:cleared events
   */
  #handleSearchCleared() {
    this.#resetSearch()
  }

  /**
   * Fetches search results using the section rendering API
   * @param {string} searchTerm - The search term
   */
  async #getSearchResults(searchTerm) {
    const sectionId = this.dataset.sectionId
    if (!sectionId) {
      console.error("PlPredictiveSearch: No data-section-id attribute found")
      return
    }

    // Create abort controller for this request and store reference
    if (this.#activeFetch) {
      this.#activeFetch.abort()
    }
    const abortController = new AbortController()
    this.#activeFetch = abortController

    // Build URL for section rendering with search query
    const url = new URL(Theme.routes.predictive_search_url, window.location.origin)
    url.searchParams.set("q", searchTerm)
    url.searchParams.set("resources[type]", "product,collection")
    url.searchParams.set("resources[limit]", "10")
    url.searchParams.set("resources[options][prefix]", "last")

    const normalizedId = normalizeSectionId(sectionId)

    try {
      const sectionHTML = await sectionRenderer.getSectionHTML(normalizedId, false, url)

      // Check if this specific request was aborted
      if (this.#activeFetch !== abortController || abortController.signal.aborted) {
        return
      }

      // Parse the section HTML and extract the content
      const parser = new DOMParser()
      const doc = parser.parseFromString(sectionHTML, "text/html")

      // Find the section element (could be shopify-section-{id} or just contain the section)
      const sectionElement =
        doc.querySelector(`#shopify-section-${normalizedId}`) ||
        doc.querySelector(`[id*="${normalizedId}"]`)

      if (!sectionElement) {
        console.error("PlPredictiveSearch: Section not found in response")
        return
      }

      // Find the results container inside the section
      const newResultsContainer = sectionElement.querySelector("[data-search-results]")
      if (!newResultsContainer) {
        console.error("PlPredictiveSearch: Results container not found in section")
        return
      }

      // Morph the entire results container to replace the drawer container
      morph(this.#resultsContainer, newResultsContainer, { childrenOnly: false })
      // Update the reference to the results container after morphing
      this.#resultsContainer = this.querySelector("[data-search-results]")
      this.#resetScrollPosition()
      this.#currentIndex = -1
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("PlPredictiveSearch: Failed to fetch search results:", error)
      }
    } finally {
      this.#activeFetch = null
    }
  }

  /**
   * Resets the search to empty state
   */
  async #resetSearch() {
    // Reset keyboard navigation
    this.#currentIndex = -1

    // Abort any pending requests
    if (this.#activeFetch) {
      this.#activeFetch.abort()
      this.#activeFetch = null
    }

    if (!this.#resultsContainer) return

    // Fetch empty state from section (without query)
    const sectionId = this.dataset.sectionId
    if (!sectionId) return

    try {
      const url = new URL((window.Shopify?.routes?.root || "/") + "search", window.location.origin)

      const sectionHTML = await sectionRenderer.getSectionHTML(
        normalizeSectionId(sectionId),
        false,
        url
      )

      if (!sectionHTML) return

      const parser = new DOMParser()
      const doc = parser.parseFromString(sectionHTML, "text/html")
      const normalizedId = normalizeSectionId(sectionId)
      const sectionElement =
        doc.querySelector(`#shopify-section-${normalizedId}`) ||
        doc.querySelector(`[id*="${normalizedId}"]`)

      if (!sectionElement) return

      const newResultsContainer = sectionElement.querySelector("[data-search-results]")
      if (newResultsContainer) {
        morph(this.#resultsContainer, newResultsContainer, { childrenOnly: false })
        this.#resultsContainer = this.querySelector("[data-search-results]")
        this.#resetScrollPosition()
        await this.#injectRecentlyViewed()
      }
    } catch (error) {
      // Silently fail on reset
    }
  }

  /**
   * Injects recently viewed products into empty state
   */
  async #injectRecentlyViewed() {
    const viewedProducts = RecentlyViewed.getProducts()
    if (viewedProducts.length === 0) return

    const sectionId = this.dataset.sectionId
    if (!sectionId) return

    // Build search query for recently viewed products
    const productQuery = viewedProducts.map((id) => `id:${id}`).join(" OR ")

    const url = new URL((window.Shopify?.routes?.root || "/") + "search", window.location.origin)
    url.searchParams.set("q", productQuery)
    url.searchParams.set("type", "product")
    url.searchParams.set("options[prefix]", "last")

    try {
      const sectionHTML = await sectionRenderer.getSectionHTML(
        normalizeSectionId(sectionId),
        false,
        url
      )

      if (!sectionHTML) return

      const parser = new DOMParser()
      const doc = parser.parseFromString(sectionHTML, "text/html")
      const normalizedId = normalizeSectionId(sectionId)
      const sectionElement =
        doc.querySelector(`#shopify-section-${normalizedId}`) ||
        doc.querySelector(`[id*="${normalizedId}"]`)

      if (!sectionElement) return

      const recentlyViewedContainer = this.#resultsContainer.querySelector(
        "[data-recently-viewed-container]"
      )
      if (!recentlyViewedContainer) return

      const productResults = sectionElement.querySelector(
        ".pl--predictive-search-results__products"
      )
      if (productResults) {
        // Add "Recently Viewed" title
        const title = document.createElement("h3")
        title.className = "pl--predictive-search-results__title"
        title.textContent = "Recently Viewed"
        recentlyViewedContainer.appendChild(title)

        // Append product results
        morph(recentlyViewedContainer, productResults, { childrenOnly: false })
      }
    } catch (error) {
      console.error("PlPredictiveSearch: Failed to inject recently viewed:", error)
    }
  }

  /**
   * Resets scroll position to top
   */
  #resetScrollPosition() {
    if (this.#resultsContainer) {
      requestAnimationFrame(() => {
        this.#resultsContainer.scrollTop = 0
      })
    }
  }

  /**
   * Gets all result items for keyboard navigation
   * @returns {HTMLElement[]} Array of result item elements
   */
  get #allResultsItems() {
    if (!this.#resultsContainer) return []

    return Array.from(
      this.#resultsContainer.querySelectorAll(
        '[data-search-result-item], a[href*="/products/"], a[href*="/collections/"]'
      )
    ).filter((item) => item instanceof HTMLElement)
  }

  /**
   * Handles keyboard navigation
   * @param {KeyboardEvent} event - Keyboard event
   */
  #handleKeyboardNavigation(event) {
    const items = this.#allResultsItems
    if (items.length === 0) return

    // Don't interfere with input typing
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      return
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.#currentIndex = this.#currentIndex < items.length - 1 ? this.#currentIndex + 1 : 0
        this.#updateSelectedItem()
        break

      case "ArrowUp":
        event.preventDefault()
        this.#currentIndex = this.#currentIndex > 0 ? this.#currentIndex - 1 : items.length - 1
        this.#updateSelectedItem()
        break

      case "Enter":
        if (this.#currentIndex >= 0 && items[this.#currentIndex]) {
          event.preventDefault()
          const link = items[this.#currentIndex].closest("a") || items[this.#currentIndex]
          if (link instanceof HTMLAnchorElement) {
            link.click()
          }
        }
        break

      case "Escape":
        // Escape handled by search input, but we can reset navigation
        this.#currentIndex = -1
        this.#updateSelectedItem()
        break
    }
  }

  /**
   * Updates the selected item for keyboard navigation
   */
  #updateSelectedItem() {
    const items = this.#allResultsItems

    items.forEach((item, index) => {
      if (index === this.#currentIndex) {
        item.setAttribute("aria-selected", "true")
        item.classList.add("pl-search-result-item--selected")
        item.scrollIntoView({ behavior: "smooth", block: "nearest" })
      } else {
        item.removeAttribute("aria-selected")
        item.classList.remove("pl-search-result-item--selected")
      }
    })
  }
}

if (!customElements.get("pl-predictive-search")) {
  customElements.define("pl-predictive-search", PlPredictiveSearch)
}

export { PlPredictiveSearch }
